import type {WrappedField} from '@cohort/merchants/components/form/v2/FieldWrapper';
import FieldWrapper from '@cohort/merchants/components/form/v2/FieldWrapper';
import {MenuBar} from '@cohort/merchants/components/rich-text-editor/MenuBar';
import {Link} from '@tiptap/extension-link';
import {Placeholder} from '@tiptap/extension-placeholder';
import {EditorContent, useEditor} from '@tiptap/react';
import {StarterKit} from '@tiptap/starter-kit';
import {useEffect} from 'react';

function generateClasses(disabled: boolean): string {
  const defaultClasses = 'p-5 prose-sm cursor-text m-0 outline-primary h-full';
  const disabledClasses = 'bg-slate-100 cursor-not-allowed text-slate-400';

  return disabled ? `${defaultClasses} ${disabledClasses}` : defaultClasses;
}

type BaseTextEditorProps = WrappedField & {
  name: string;
  value: string;
  onChange: (value: string | null) => void;
  onBlur: () => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  optional?: boolean;
};

const BaseTextEditor: React.FC<BaseTextEditorProps> = ({
  label,
  description,
  name,
  placeholder,
  disabled,
  value,
  error,
  onChange,
  onBlur,
  optional,
}) => {
  const editor = useEditor({
    content: value,
    editable: !disabled,
    extensions: [
      StarterKit,
      Link.configure({
        linkOnPaste: true,
        openOnClick: true,
      }),
      Placeholder.configure({placeholder}),
    ],
    onUpdate: ({editor}) => {
      onChange(editor.isEmpty ? null : editor.getHTML());
    },
    onBlur: () => onBlur(),
  });
  useEffect(() => {
    if (editor && error) {
      editor.commands.focus();
    }
  }, [error, editor]);

  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editorProps: {
          attributes: {
            class: generateClasses(disabled ?? false),
            'data-testid': name,
          },
        },
      });
    }
  }, [disabled, editor, name]);

  return (
    <FieldWrapper
      label={label}
      name={name}
      description={description}
      error={error}
      optional={optional}
    >
      <div className="grid h-full w-full rounded-md border bg-white text-sm shadow-sm [grid-template-rows:min-content_1fr]">
        {editor !== null && <MenuBar editor={editor} disabled={disabled} />}
        <EditorContent name={name} editor={editor} />
      </div>
    </FieldWrapper>
  );
};

export default BaseTextEditor;
